export default function IntegrationGuide() {

  const baseUrl = import.meta.env.VITE_URL || ''

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-6 text-center">
          Token Map - Integration Guide
        </h1>

        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
          <p className="text-blue-800">
            <strong>Note:</strong> Replace <code>${baseUrl}</code> with your actual domain URL in all examples below.
          </p>
        </div>

        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <p className="text-yellow-800">
            <strong>Note:</strong> The data in the application is not complete, it was created by processing a file with partial statistics and random supplementation of data about area and number of planted trees and collected seed material.
          </p>
        </div>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 border-b-2 border-blue-500 pb-2">
            1. Basic Integration
          </h2>

          <h3 className="text-xl font-medium text-gray-700 mb-3">Simple Embedding</h3>
          <div className="bg-gray-100 p-4 rounded-lg mb-4 font-mono text-sm overflow-x-auto">
            <pre>{`<iframe 
  src="${baseUrl}" 
  width="100%" 
  height="600px" 
  frameborder="0"
  allowfullscreen>
</iframe>`}</pre>
          </div>

          <div className="border border-gray-300 rounded-lg overflow-hidden mb-6">
            <iframe
              src={`${baseUrl}`}
              width="100%"
              height="400px"
              frameBorder="0"
              allowFullScreen
              title="Basic Integration Example">
            </iframe>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 border-b-2 border-blue-500 pb-2">
            2. View Modes
          </h2>

          <h3 className="text-xl font-medium text-gray-700 mb-3">Map Only - No Header, No Footer, No Navigation</h3>
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
            <p className="text-blue-800">
              <strong>Note:</strong> Pay attention to the URL change <strong>${baseUrl}/map</strong> the application will display a page with a map without navigation to other pages. For more information, see section 5, parameter 'route'
            </p>
          </div>

          <div className="bg-gray-100 p-4 rounded-lg mb-4 font-mono text-sm overflow-x-auto">
            <pre>{`<iframe 
  src="${baseUrl}/map?view=map&hideHeader=true&hideFooter=true&hideNavigation=true" 
  width="100%" 
  height="600px" 
  frameborder="0">
</iframe>`}</pre>
          </div>

          <div className="border border-gray-300 rounded-lg overflow-hidden mb-6">
            <iframe
              src={`${baseUrl}/map?view=map&hideHeader=true&hideFooter=true&hideNavigation=true`}
              width="100%"
              height="400px"
              frameBorder="0"
              title="Map Only Example">
            </iframe>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 border-b-2 border-blue-500 pb-2">
            3. Color Customization
          </h2>

          <h3 className="text-xl font-medium text-gray-700 mb-3">Predefined Themes</h3>

          <h4 className="text-lg font-medium text-gray-600 mb-2">Custom Colors - Blue Shades</h4>
          <div className="bg-gray-100 p-4 rounded-lg mb-4 font-mono text-sm overflow-x-auto">
            <pre>{`<iframe 
  src="${baseUrl}/map?forestColor=%231e3a8a&seedColor=%23dbeafe&backgroundColor=%2360a5fa&mainBackgroundColor=%23f0f9ff" 
  width="100%" 
  height="600px" 
  frameborder="0">
</iframe>`}</pre>
          </div>

          <div className="border border-gray-300 rounded-lg overflow-hidden mb-6">
            <iframe
              src={`${baseUrl}/map?forestColor=%231e3a8a&seedColor=%23dbeafe&backgroundColor=%2360a5fa&mainBackgroundColor=%23f0f9ff`}
              width="100%"
              height="400px"
              frameBorder="0"
              title="Blue Theme Example">
            </iframe>
          </div>

          <h4 className="text-lg font-medium text-gray-600 mb-2">Custom Colors - Gray Shades</h4>
          <div className="bg-gray-100 p-4 rounded-lg mb-4 font-mono text-sm overflow-x-auto">
            <pre>{`<iframe 
  src="${baseUrl}/map?forestColor=%23374151&seedColor=%23f3f4f6&backgroundColor=%239ca3af&mainBackgroundColor=%23f9fafb" 
  width="100%" 
  height="600px" 
  frameborder="0">
</iframe>`}</pre>
          </div>

          <div className="border border-gray-300 rounded-lg overflow-hidden mb-6">
            <iframe
              src={`${baseUrl}/map?forestColor=%23374151&seedColor=%23f3f4f6&backgroundColor=%239ca3af&mainBackgroundColor=%23f9fafb`}
              width="100%"
              height="400px"
              frameBorder="0"
              title="Gray Theme Example">
            </iframe>
          </div>

          <h4 className="text-lg font-medium text-gray-600 mb-2">Custom Colors - Original Green Theme</h4>
          <div className="bg-gray-100 p-4 rounded-lg mb-4 font-mono text-sm overflow-x-auto">
            <pre>{`<iframe 
  src="${baseUrl}/map?forestColor=%232d5016&seedColor=%23f4f1e8&backgroundColor=%238fbc8f" 
  width="100%" 
  height="600px" 
  frameborder="0">
</iframe>`}</pre>
          </div>

          <div className="border border-gray-300 rounded-lg overflow-hidden mb-6">
            <iframe
              src={`${baseUrl}/map?forestColor=%232d5016&seedColor=%23f4f1e8&backgroundColor=%238fbc8f`}
              width="100%"
              height="400px"
              frameBorder="0"
              title="Green Theme Example">
            </iframe>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 border-b-2 border-blue-500 pb-2">
            4. Advanced Examples
          </h2>

          <h3 className="text-xl font-medium text-gray-700 mb-3">Complete Customization</h3>
          <div className="bg-gray-100 p-4 rounded-lg mb-4 font-mono text-sm overflow-x-auto">
            <pre>{`<iframe 
  src="${baseUrl}/map?view=map&theme=nature&hideHeader=true&hideFooter=true&hideNavigation=true" 
  width="100%" 
  height="600px" 
  frameborder="0">
</iframe>`}</pre>
          </div>

          <div className="border border-gray-300 rounded-lg overflow-hidden mb-6">
            <iframe
              src={`${baseUrl}/map?view=map&theme=nature&hideHeader=true&hideFooter=true&hideNavigation=true`}
              width="100%"
              height="400px"
              frameBorder="0"
              title="Complete Customization Example">
            </iframe>
          </div>

          <h3 className="text-xl font-medium text-gray-700 mb-3">Responsive Design</h3>
          <div className="bg-gray-100 p-4 rounded-lg mb-4 font-mono text-sm overflow-x-auto">
            <pre>{`<div class="responsive-iframe">
  <iframe src="${baseUrl}/map?view=map&theme=ocean"></iframe>
</div>`}</pre>
          </div>

          <div className="relative w-full h-0 pb-[56.25%] mb-6">
            <iframe
              src={`${baseUrl}/map?view=map&theme=ocean`}
              className="absolute top-0 left-0 w-full h-full border-0"
              title="Responsive Design Example">
            </iframe>
          </div>

          <h3 className="text-xl font-medium text-gray-700 mb-3">Multiple Instances</h3>
          <div className="bg-gray-100 p-4 rounded-lg mb-4 font-mono text-sm overflow-x-auto">
            <pre>{`<div class="multi-instance">
  <div>
    <h3>Map View</h3>
    <iframe src="${baseUrl}/map?view=map&theme=nature" width="100%" height="400px" frameborder="0"></iframe>
  </div>
  <div>
    <h3>Statistics View</h3>
    <iframe src="${baseUrl}/map?view=stats&theme=minimal" width="100%" height="400px" frameborder="0"></iframe>
  </div>
</div>`}</pre>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="border border-gray-300 rounded-lg p-4">
              <h3 className="text-lg font-medium text-center mb-3">Map View</h3>
              <iframe
                src={`${baseUrl}/map?view=map&theme=nature"`}
                width="100%"
                height="300px"
                frameBorder="0"
                title="Map View Example">
              </iframe>
            </div>

            <div className="border border-gray-300 rounded-lg p-4">
              <h3 className="text-lg font-medium text-center mb-3">Statistics View</h3>
              <iframe
                src={`${baseUrl}/map?view=stats&theme=minimal`}
                width="100%"
                height="300px"
                frameBorder="0"
                title="Statistics View Example">
              </iframe>
            </div>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 border-b-2 border-blue-500 pb-2">
            5. Parameter Reference
          </h2>

          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left border-b">Parameter</th>
                  <th className="px-4 py-2 text-left border-b">Values</th>
                  <th className="px-4 py-2 text-left border-b">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="px-4 py-2 border-b">view</td>
                  <td className="px-4 py-2 border-b">map, stats</td>
                  <td className="px-4 py-2 border-b">Sets view mode (map-only or statistics-only)</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 border-b">hideHeader</td>
                  <td className="px-4 py-2 border-b">true, false</td>
                  <td className="px-4 py-2 border-b">Hides the application header</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 border-b">hideFooter</td>
                  <td className="px-4 py-2 border-b">true, false</td>
                  <td className="px-4 py-2 border-b">Hides the application footer</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 border-b">hideNavigation</td>
                  <td className="px-4 py-2 border-b">true, false</td>
                  <td className="px-4 py-2 border-b">Hides the navigation menu</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 border-b">route</td>
                  <td className="px-4 py-2 border-b">/, /map, /statistics</td>
                  <td className="px-4 py-2 border-b">Sets the initial page to load</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 border-b">theme</td>
                  <td className="px-4 py-2 border-b">nature, ocean, earth, forest, minimal</td>
                  <td className="px-4 py-2 border-b">Applies a preset color theme</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 border-b">forestColor</td>
                  <td className="px-4 py-2 border-b">Hex, RGB, HSL, or named color</td>
                  <td className="px-4 py-2 border-b">Custom forest color</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 border-b">seedColor</td>
                  <td className="px-4 py-2 border-b">Hex, RGB, HSL, or named color</td>
                  <td className="px-4 py-2 border-b">Custom seed color</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 border-b">backgroundColor</td>
                  <td className="px-4 py-2 border-b">Hex, RGB, HSL, or named color</td>
                  <td className="px-4 py-2 border-b">Custom background color</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 border-b">mainBackgroundColor</td>
                  <td className="px-4 py-2 border-b">Hex, RGB, HSL, or named color</td>
                  <td className="px-4 py-2 border-b">Custom main background color</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 border-b">debug</td>
                  <td className="px-4 py-2 border-b">true</td>
                  <td className="px-4 py-2 border-b">Enables debug logging in browser console</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 border-b-2 border-blue-500 pb-2">
            6. Best Practices
          </h2>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
            <p className="text-blue-800 font-medium mb-2">Best Practices:</p>
            <ul className="text-blue-800 list-disc list-inside space-y-1">
              <li>Always set appropriate dimensions for your iframe</li>
              <li>Use responsive design for mobile compatibility</li>
              <li>Test different themes to match your website's design</li>
              <li>Consider performance using view modes to load only necessary components</li>
              <li>Ensure your iframe source URL is secure (HTTPS)</li>
            </ul>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 border-b-2 border-blue-500 pb-2">
            7. Troubleshooting
          </h2>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <p className="text-yellow-800 font-medium mb-2">Common Issues:</p>
            <ul className="text-yellow-800 list-disc list-inside space-y-1">
              <li><strong>Blank iframe:</strong> Check that the URL is correct and accessible</li>
              <li><strong>Styling issues:</strong> Ensure your CSS doesn't conflict with iframe styles</li>
              <li><strong>Mobile responsiveness:</strong> Test on different devices and screen sizes</li>
            </ul>
          </div>
        </section>

        <div className="text-center mt-8 p-6 bg-gray-50 rounded-lg">
          <p className="text-gray-700 font-medium">Token Map Iframe Integration Guide</p>
          <p className="text-gray-600 text-sm mt-2">
            Replace <code>{`${baseUrl}`}</code> with your actual domain URL to test integrations.
          </p>

          <div className="mt-6">
            <button
              onClick={() => {
                // Create a download link for the test-iframe.html file
                const link = document.createElement('a');
                link.href = '/test-iframe.html';
                link.download = 'test-iframe.html';
                link.textContent = 'Download Integration Guide';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg text-lg shadow-lg transition-colors duration-200 inline-flex items-center gap-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download Integration Guide
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
